import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { environment } from '@env';
import { CommsService } from '../comms/comms.service';

/**
 * TracingService initializes OpenTelemetry Web SDK for distributed tracing
 *
 * This service:
 * - Creates browser-side trace spans for user interactions
 * - Automatically injects W3C Trace Context headers (traceparent) into HTTP requests
 * - Exports spans to the REST API, which forwards them to Grafana Tempo
 * - Enables end-to-end trace correlation: Browser → API → Workers
 *
 * Usage:
 * - Automatically initialized in AppModule via APP_INITIALIZER
 * - No manual span creation needed - FetchInstrumentation handles API calls
 * - Traces visible in Grafana Tempo at https://logging.corvexconnected.com
 */
@Injectable({
  providedIn: 'root'
})
export class TracingService {
  private provider: WebTracerProvider | null = null;
  private currentEndpoint: string | null = null;
  private tracingStarted = false;

  constructor(
    private router: Router,
    private commsService: CommsService
  ) {}

  /**
   * Initialize OpenTelemetry Web SDK
   *
   * This must be called BEFORE any HTTP requests are made.
   * Typically called from APP_INITIALIZER in AppModule.
   */
  initialize(): void {
    try {
      // Get current endpoint from CommsService (updates dynamically)
      const endpoint = this.commsService.backendHost;
      const servicePath = this.commsService.servicePath;
      const subscriberID = this.commsService.subscriberID;

      // Skip initialization if no endpoint available yet (will be called again after discovery)
      if (!endpoint) {
        return;
      }

      // Skip initialization if tracing is disabled in config
      if ((environment.data as any)?.enableTracing === false) {
        return;
      }

      // Skip if this is the registry endpoint - wait for real subscriber endpoint
      if (endpoint.includes('registry.s3rv1c3s') || endpoint.includes('registry2-s3rv1c3s')) {
        return;
      }

      // Skip re-initialization if already started with same endpoint
      if (this.tracingStarted && this.currentEndpoint === endpoint) {
        return;
      }

      // Allow re-initialization if endpoint changed
      if (this.tracingStarted && this.currentEndpoint !== endpoint) {
        this.provider = null;
        this.tracingStarted = false;
      }

      this.currentEndpoint = endpoint;

      // Create resource with service metadata
      const resource = resourceFromAttributes({
        'service.name': 'corvex-webapp',
        'deployment.environment': environment.production ? 'production' : 'development',
        'app.version': environment.appVersion,
        'subscriber.id': subscriberID?.toString() || 'unknown',
      });

      // Create OTLP exporter pointing to REST API
      // The REST API will receive spans at traces.cgi and forward to Tempo
      // Note: OTLPTraceExporter normalizes URLs, so we use 'traces' which maps to traces.cgi
      const tracesUrl = `${endpoint}${servicePath}/traces`;

      const exporter = new OTLPTraceExporter({
        url: tracesUrl,
        headers: {
          // Add any required authentication headers here
          // For now, relying on CORS and existing auth mechanisms
        },
      });

      // Create and register tracer provider
      // Pass the batch processor in the constructor
      this.provider = new WebTracerProvider({
        resource: resource,
        spanProcessors: [new BatchSpanProcessor(exporter, {
          maxQueueSize: 100,
          maxExportBatchSize: 10,
          scheduledDelayMillis: 5000, // Send batch every 5 seconds
        })],
      });

      this.provider.register();

      // Instrument both fetch API and XMLHttpRequest (used by Angular HttpClient)
      registerInstrumentations({
        instrumentations: [
          new FetchInstrumentation({
            // Ignore asset requests and trace export to prevent circular tracing
            ignoreUrls: [/^\.\/assets\//, /\/traces$/],

            // Propagate trace headers to all URLs
            propagateTraceHeaderCorsUrls: [/.*/],

            // Clear timing resources to avoid memory leaks
            clearTimingResources: true,

            // Add custom attributes to fetch spans
            applyCustomAttributesOnSpan: (span, request, response) => {
              // Only add attributes if request/response are available
              if (request instanceof Request) {
                span.setAttribute('http.url', request.url);
                span.setAttribute('http.method', request.method);

                // Extract API endpoint from URL and update span name
                const apiEndpoint = this.extractApiEndpoint(request.url);
                if (apiEndpoint) {
                  span.setAttribute('api.endpoint', apiEndpoint);

                  // Get current route for context
                  const currentRoute = this.router.url;
                  const routeName = currentRoute ? this.getReadableRouteName(currentRoute) : 'Unknown';

                  // Update span name: "Route - Method API"
                  span.updateName(`${routeName} - ${request.method} ${apiEndpoint}`);
                }
              }
              if (response instanceof Response) {
                span.setAttribute('http.status_code', response.status);
              }

              // Extract user.id and session from token (format: <userID>-<uuid>)
              const token = this.commsService.token;
              if (token) {
                const parts = token.split('-');
                if (parts.length >= 2) {
                  const userID = parts[0];
                  const sessionUUID = parts.slice(1).join('-'); // Handle UUIDs with multiple dashes
                  span.setAttribute('user.id', userID);
                  span.setAttribute('session.id', sessionUUID);
                }
              }

              // Add subscriber ID if available
              const subscriberID = this.commsService.subscriberID;
              if (subscriberID) {
                span.setAttribute('subscriber.id', subscriberID.toString());
              }
            },
          }),
          new XMLHttpRequestInstrumentation({
            // Ignore asset requests and trace export to prevent circular tracing
            ignoreUrls: [/^\.\/assets\//, /\/traces$/],

            // Propagate trace headers to all URLs
            propagateTraceHeaderCorsUrls: [/.*/],

            // Clear timing resources to avoid memory leaks
            clearTimingResources: true,

            // Enhance span names with route context
            applyCustomAttributesOnSpan: (span, xhr) => {
              // Add current route to span attributes
              const currentRoute = this.router.url;
              const routeName = currentRoute ? this.getReadableRouteName(currentRoute) : 'Unknown';

              if (currentRoute) {
                span.setAttribute('route.name', routeName);
                span.setAttribute('route.url', currentRoute);
              }

              // Extract API endpoint from XHR URL
              const url = xhr?.responseURL || '';
              const apiEndpoint = this.extractApiEndpoint(url);

              if (apiEndpoint) {
                span.setAttribute('api.endpoint', apiEndpoint);
                // Update span name: "Route - Method API"
                // XMLHttpRequestInstrumentation sets the span name to "HTTP <METHOD>"
                const spanName = span['name'] || '';
                const method = spanName.replace('HTTP ', '') || 'HTTP';
                span.updateName(`${routeName} - ${method} ${apiEndpoint}`);
              } else {
                // Fallback to original behavior
                span.updateName(`${routeName} - ${span['name'] || 'HTTP Request'}`);
              }

              // Extract user.id and session from token (format: <userID>-<uuid>)
              const token = this.commsService.token;
              if (token) {
                const parts = token.split('-');
                if (parts.length >= 2) {
                  const userID = parts[0];
                  const sessionUUID = parts.slice(1).join('-'); // Handle UUIDs with multiple dashes
                  span.setAttribute('user.id', userID);
                  span.setAttribute('session.id', sessionUUID);
                }
              }

              // Add subscriber ID if available
              const subscriberID = this.commsService.subscriberID;
              if (subscriberID) {
                span.setAttribute('subscriber.id', subscriberID.toString());
              }
            },
          }),
        ],
      });


      this.tracingStarted = true;

    } catch (error) {
      console.error('[TracingService] Failed to initialize OpenTelemetry:', error);
      this.tracingStarted = false;
      // Don't throw - tracing is observability, not critical functionality
    }
  }

  /**
   * Extract API endpoint from URL
   * https://example.com/scripts/getObservations.cgi?param=value → getObservations.cgi
   * /api/v2/users → users
   */
  private extractApiEndpoint(url: string): string {
    try {
      // Parse URL to get pathname
      const urlObj = new URL(url, window.location.origin);
      const pathname = urlObj.pathname;

      // Split by / and get the last segment
      const segments = pathname.split('/').filter(s => s.length > 0);
      if (segments.length === 0) {
        return '';
      }

      const lastSegment = segments[segments.length - 1];

      // For REST-style endpoints, return the last path segment
      // e.g., /api/v2/users → users
      return lastSegment;
    } catch (error) {
      // If URL parsing fails, return empty string
      return '';
    }
  }

  /**
   * Convert route URL to readable name
   * /pages/dashboard/asset → Dashboard - Asset
   * /corvex → Corvex
   */
  private getReadableRouteName(url: string): string {
    // Remove query parameters
    let route = url.split('?')[0];

    // Remove leading/trailing slashes
    route = route.replace(/^\/+|\/+$/g, '');

    // If empty, it's the root route
    if (!route) {
      return 'Unknown';
    }

    // Split by slashes and capitalize
    const parts = route.split('/');

    // Common patterns:
    // pages/dashboard/asset → Dashboard - Asset
    // corvex → Corvex
    if (parts[0] === 'pages') {
      parts.shift(); // Remove 'pages'
    }

    // Capitalize and join with dashes
    const readable = parts
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' - ');

    return readable;
  }

  /**
   * Shutdown the tracer provider
   * This ensures all pending spans are flushed before app closes
   */
  async shutdown(): Promise<void> {
    if (this.provider) {
      try {
        await this.provider.shutdown();
      } catch (error) {
        console.error('[TracingService] Error during shutdown:', error);
      }
    }
  }
}
